import { ProductType, SubProductType, Tags } from '@hc-frontend/core-types';
import { FormButton } from '@hc-frontend/core-ui';
import { ButtonWithTracking } from '@hc-frontend/shells-shared';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import type { RefObject } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { DisclaimerBottom } from '../../../templates/disclaimer/disclaimer';
import { FieldNames } from './constants';
import secureLogo from './secure-logo.svg';

export function ConsentedCTA({
  product,
  ctaText,
  formRef,
  isValid,
  pathToSkip,
  metricTags,
  tcpaConsent,
  showPartialForm = false,
  isPending,
  showSkipButton,
  subProduct,
}: {
  product: ProductType;
  ctaText: string;
  formRef: RefObject<HTMLFormElement | null>;
  isValid: boolean;
  pathToSkip?: string;
  metricTags: Tags;
  tcpaConsent: string;
  showPartialForm?: boolean;
  isPending: boolean;
  showSkipButton?: boolean;
  subProduct?: SubProductType;
}) {
  const [isSkipClick, setIsSkipClick] = useState(false);
  const [disclosure, setDisclosure] = useState<string>('');
  const disclaimerTopAboveCTA = product === ProductType.Enum.HEALTH;

  const disclaimerBottom = DisclaimerBottom[product];
  const router = useRouter();

  const disclosureCtaMessage = `By clicking the ${ctaText} button, I agree to the consents below the button.`;

  const [showDisclaimerTop, setShowDisclaimerTop] = useState(false);

  const tcpaConsentRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setDisclosure(`${node?.textContent}`);
    }
  }, []);

  const ctaClassnames =
    product === ProductType.Enum.HEALTH || product === ProductType.Enum.MEDICARE
      ? 'w-full justify-center px-32-32 py-12-12'
      : 'px-32-32 py-12-12';

  useEffect(() => {
    if (formRef) {
      const element = formRef.current?.elements.namedItem(
        FieldNames.PHONE_NUMBER,
      ) as HTMLInputElement;

      const listener = () => {
        setShowDisclaimerTop(true);
      };
      if (element) {
        element.addEventListener('input', listener);
        return () => {
          element.removeEventListener('input', listener);
        };
      }
    }

    return undefined;
  }, [formRef]);

  const getDisclaimerTop = () => {
    const className =
      product === ProductType.Enum.HEALTH
        ? 'hc-box-layout hc-typography--disclaimer'
        : 'hc-box-layout hc-typography--disclaimer bg-primary-background-50 px-8-16 py-8-12';

    if (!showDisclaimerTop) {
      return null;
    }

    return (
      <div
        className={className}
        ref={tcpaConsentRef}
        dangerouslySetInnerHTML={{ __html: tcpaConsent }}
      />
    );
  };

  const getSubmitDisabled = () => {
    if (showPartialForm) {
      return !isValid || isPending || isSkipClick;
    }

    return isPending || isSkipClick;
  };

  const showMedicareAgentMessage =
    (product === 'MEDICARE' && !subProduct) || product === 'MEDICARE_ADVANTAGE';

  return (
    <>
      {disclaimerTopAboveCTA ? null : (
        <>
          {showDisclaimerTop ? (
            <span className="hc-typography--disclaimer text-center">
              {disclosureCtaMessage}
            </span>
          ) : (
            true
          )}

          <div className="hc-cluster-layout justify-center">
            <FormButton
              isPending={isPending}
              id="submit-lead"
              className={ctaClassnames}
              disabled={getSubmitDisabled()}
            >
              {ctaText}
            </FormButton>
          </div>

          {showSkipButton && pathToSkip ? (
            <div className="hc-center-layout hc-stack-layout my-16-16">
              <ButtonWithTracking
                id="skip-button"
                variant="text"
                type="submit"
                className="font-regular text-gray-400 underline"
                metricTags={metricTags}
                disabled={isPending}
                isLoading={isSkipClick}
                onClick={(event) => {
                  event.preventDefault();
                  setIsSkipClick(true);

                  router.push(pathToSkip);
                }}
                name="action"
                value="skip"
              >
                Skip
              </ButtonWithTracking>
            </div>
          ) : null}
        </>
      )}

      <label
        className="flex flex-col items-center gap-24-24"
        id="leadid_tcpa_disclosure_label"
        htmlFor="leadid_tcpa_disclosure"
      >
        {/* TODO: Verify if this is still used, otherwise delete it */}
        <input
          type="hidden"
          id="leadid_tcpa_disclosure"
          name="leadid_tcpa_disclosure"
        />

        {showMedicareAgentMessage ? (
          <p>
            We can connect you with a licensed insurance agent, or we will
            provide access to Medicare plans that may be available in your
            service area.
          </p>
        ) : null}

        {getDisclaimerTop()}

        {disclaimerTopAboveCTA ? (
          <>
            <FormButton
              isPending={isPending}
              id="submit-lead"
              className={ctaClassnames}
              disabled={getSubmitDisabled()}
            >
              {ctaText}
            </FormButton>
            {showSkipButton && pathToSkip ? (
              <div className="hc-center-layout hc-stack-layout my-16-16">
                <ButtonWithTracking
                  id="skip-button"
                  variant="text"
                  type="submit"
                  className="font-regular text-gray-400 underline"
                  metricTags={metricTags}
                  disabled={isPending}
                  isLoading={isSkipClick}
                  onClick={(event) => {
                    event.preventDefault();
                    setIsSkipClick(true);
                    router.push(pathToSkip);
                  }}
                  name="action"
                  value="skip"
                >
                  Skip
                </ButtonWithTracking>
              </div>
            ) : null}
          </>
        ) : null}

        <Image
          src={secureLogo.src}
          alt="Secure Logo"
          width={155}
          height={53}
          priority
        />
        {disclaimerBottom ? (
          <div className="hc-typography--disclaimer">{disclaimerBottom}</div>
        ) : null}
      </label>

      <input
        type="hidden"
        name={FieldNames.DISCLOSURE}
        // TODO: pass a simple id instead of the full TCPA
        value={disclosure}
      />
    </>
  );
}
